import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import React, { useState } from 'react';
import {
  useGetAvailableJobsQuery,
  useTriggerJobMutation,
  useListJobsQuery,
} from 'store/app/entities/BatchSlice';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import alertActions from 'store/app/ui/Alert/action';
import { I18n } from 'utils/i18n';

const TriggerJobDialog = ({ isOpen, onClose, actions }) => {
  const [jobName, setJobName] = useState('');
  const [batchConfigId, setBatchConfigId] = useState('');
  const [useLastExecutedAt, setUseLastExecutedAt] = useState(true);
  const [jobData, setJobData] = useState('');
  const [jobDataError, setJobDataError] = useState('');

  const { data: availableJobs } = useGetAvailableJobsQuery();
  const { data: jobsData } = useListJobsQuery();
  const [triggerJob, { isLoading }] = useTriggerJobMutation();

  // Handle different response structures and use listJobs as fallback
  let jobNames = [];
  if (availableJobs) {
    if (Array.isArray(availableJobs)) {
      jobNames = availableJobs;
    } else if (availableJobs.jobs && Array.isArray(availableJobs.jobs)) {
      jobNames = availableJobs.jobs;
    }
  }

  // Fallback: extract job names from listJobs query
  if (jobNames.length === 0 && jobsData?.jobs) {
    jobNames = jobsData.jobs.map(job => job.jobName || job.name).filter(Boolean);
  }

  const handleClose = () => {
    setJobName('');
    setBatchConfigId('');
    setUseLastExecutedAt(true);
    setJobData('');
    setJobDataError('');
    onClose();
  };

  const handleJobDataChange = event => {
    const value = event.target.value;
    setJobData(value);
    if (value.trim()) {
      try {
        JSON.parse(value);
        setJobDataError('');
      } catch {
        setJobDataError('Invalid JSON format');
      }
    } else {
      setJobDataError('');
    }
  };

  const handleTrigger = async () => {
    if (!jobName) return;

    const body = {
      useLastExecutedAt,
    };
    if (batchConfigId) {
      body.batchConfigId = batchConfigId;
    }
    if (jobData.trim()) {
      try {
        body.jobData = JSON.parse(jobData);
      } catch {
        setJobDataError('Invalid JSON format');
        return;
      }
    }

    try {
      await triggerJob({ jobName, body }).unwrap();
      actions.alert.show({
        variant: 'success',
        label: I18n.t('AdminBatchScene.JobTriggered') || 'Job Triggered',
        message:
          I18n.t('AdminBatchScene.JobQueuedSuccessfully') || 'Job has been queued successfully',
      });
      handleClose();
    } catch (error) {
      actions.alert.show({
        variant: 'error',
        label: I18n.t('AdminBatchScene.Error') || 'Error',
        message:
          error?.data?.message ||
          I18n.t('AdminBatchScene.FailedToTriggerJob') ||
          'Failed to trigger job',
      });
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{I18n.t('AdminBatchScene.TriggerJob') || 'Trigger Batch Job'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{I18n.t('AdminBatchScene.SelectJob') || 'Select Job'}</InputLabel>
            <Select
              value={jobName}
              onChange={e => setJobName(e.target.value)}
              label={I18n.t('AdminBatchScene.SelectJob') || 'Select Job'}
            >
              {jobNames.map(name => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label={I18n.t('AdminBatchScene.BatchConfigId') || 'Batch Config ID (Optional)'}
            value={batchConfigId}
            onChange={e => setBatchConfigId(e.target.value)}
            fullWidth
            helperText={
              I18n.t('AdminBatchScene.BatchConfigIdHelper') || 'Optional: specify a batch config ID'
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={useLastExecutedAt}
                onChange={e => setUseLastExecutedAt(e.target.checked)}
              />
            }
            label={
              I18n.t('AdminBatchScene.UseLastExecutedAt') ||
              'Use lastExecutedAt for incremental sync (recommended)'
            }
          />

          <Box>
            <TextField
              size="small"
              label={I18n.t('AdminBatchScene.CustomJobData') || 'Custom Job Data (JSON, Optional)'}
              value={jobData}
              onChange={handleJobDataChange}
              fullWidth
              multiline
              rows={6}
              error={!!jobDataError}
              helperText={
                jobDataError ||
                I18n.t('AdminBatchScene.CustomJobDataHelper') ||
                'Optional: provide custom JSON data for the job'
              }
              sx={{ fontFamily: 'monospace' }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{I18n.t('AdminBatchScene.Cancel') || 'Cancel'}</Button>
        <Button
          onClick={handleTrigger}
          variant="contained"
          disabled={!jobName || isLoading || !!jobDataError}
        >
          {isLoading
            ? I18n.t('AdminBatchScene.Triggering') || 'Triggering...'
            : I18n.t('AdminBatchScene.Trigger') || 'Trigger'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: {
    alert: bindActionCreators(alertActions, dispatch),
  },
});

export default connect(null, mapDispatchToProps)(TriggerJobDialog);
